import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/layouts/AdminLayout";
import { StatusBadge, PriorityTag, BuDot } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { STATUS } from "@/lib/constants";
import { fmtSchedule } from "@/lib/format";
import { cn } from "@/lib/utils";

const ALL = "all";

const FilterSelect = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full sm:w-52" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const StatStrip = ({ stats, status, setStatus }) => (
  <div className="fade-up mb-6 grid grid-cols-3 gap-2 sm:grid-cols-5 xl:grid-cols-9" data-testid="wo-stats">
    {Object.entries(STATUS).map(([code, s]) => (
      <button key={code} onClick={() => setStatus(status === code ? ALL : code)} data-testid={`wo-stat-${code}`}
        className={cn("rounded-lg border bg-card/50 px-3 py-3 text-left transition-colors hover:border-primary/40", status === code ? "border-primary/60 bg-primary/5" : "border-border")}>
        <div className="font-display text-2xl font-bold">{stats?.by_status?.[code] || 0}</div>
        <div className="truncate text-[11px] text-muted-foreground">{s.label}</div>
      </button>
    ))}
  </div>
);

export default function WorkOrders() {
  const { meta, can } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [techs, setTechs] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(ALL);
  const [bu, setBu] = useState(ALL);
  const [tech, setTech] = useState(ALL);

  useEffect(() => {
    api.get("/work-orders/stats").then((r) => setStats(r.data));
    api.get("/users/technicians").then((r) => setTechs(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { q: q || undefined, status: status !== ALL ? status : undefined, business_unit_code: bu !== ALL ? bu : undefined, technician_id: tech !== ALL ? tech : undefined };
      api.get("/work-orders", { params }).then((r) => setOrders(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [q, status, bu, tech]);

  return (
    <div>
      <PageHeader eyebrow={`${stats?.today ?? 0} OS con fecha de hoy (sin cerrar)`} title="Órdenes de servicio">
        {can("wo.write") && <><Button asChild variant="outline" className="h-10" data-testid="new-work-order-button"><Link to="/admin/os/nueva?proyecto=1"><Plus className="mr-1 h-4 w-4" /> OS por Proyecto</Link></Button><Button asChild className="h-10" data-testid="new-normal-wo-button"><Link to="/admin/os/nueva?normal=1"><Plus className="mr-1 h-4 w-4" /> Orden de Servicio</Link></Button></>}
      </PageHeader>
      <StatStrip stats={stats} status={status} setStatus={setStatus} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <div className="relative flex-1 sm:min-w-[240px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar folio, cliente, proyecto o dirección" className="h-10 pl-9" data-testid="wo-search-input" />
        </div>
        <FilterSelect value={status} onChange={setStatus} placeholder="Todos los estados" testid="wo-filter-status" options={Object.entries(STATUS).map(([v, s]) => ({ value: v, label: s.label }))} />
        <FilterSelect value={bu} onChange={setBu} placeholder="Todas las unidades" testid="wo-filter-bu" options={(meta?.business_units || []).map((b) => ({ value: b.code, label: b.name }))} />
        <FilterSelect value={tech} onChange={setTech} placeholder="Todos los técnicos" testid="wo-filter-tech" options={techs.map((t) => ({ value: t.id, label: t.name }))} />
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-card/30">
        <Table data-testid="wo-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {["Folio", "Cliente / proyecto", "Servicio", "Técnico", "Programada", "Prioridad", "Estado"].map((h) => (
                <TableHead key={h} className="font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No hay órdenes de servicio</TableCell></TableRow>}
            {orders.map((o) => (
              <TableRow key={o.id} className="cursor-pointer" onClick={() => nav(`/admin/os/${o.id}`)} data-testid={`wo-row-${o.folio}`}>
                <TableCell className="whitespace-nowrap font-mono text-sm font-semibold text-primary">{o.folio}</TableCell>
                <TableCell><div className="font-medium">{o.client_name}</div>{o.project_name && <div className="text-xs text-muted-foreground">{o.project_name}</div>}</TableCell>
                <TableCell><BuDot meta={meta} code={o.business_unit_code} name={o.business_unit_name} /><div className="text-xs text-muted-foreground">{o.service_type_name}{(o.extra_services || []).filter((s) => !s.removed).length > 0 && <span className="ml-1 rounded border border-border px-1" data-testid={`wo-row-extra-${o.id}`}>+{(o.extra_services || []).filter((s) => !s.removed).length} servicio(s)</span>}</div></TableCell>
                <TableCell className="text-sm">{o.technician_name || <span className="text-muted-foreground">Sin asignar</span>}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs">{fmtSchedule(o.scheduled_date, o.scheduled_time)}</TableCell>
                <TableCell><PriorityTag priority={o.priority} /></TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
